import React, { useState, useCallback, useEffect } from 'react';

import { apiContainer } from '@vlabs/api-bindings';
import { PhotoUploadForm } from '@vlabs/shared/components/photo-upload-form';
import { fromAPI } from '@vlabs/shared/requests/detect-on-image-request';
import { Control, Divider, Page } from '@vlabs/uikit';
import { useTranslation } from 'react-i18next';

import { SimilarityTag } from '@vlabs/shared/components/similarity-tag/SimilarityTag';
import { estimateFace } from '@vlabs/shared/requests/estimateFace';

import st from './VerificationForm.module.sass';

function ImageSelector({ setSelectedFace }) {
  const { t } = useTranslation();
  const [, setSelected] = useState();
  const [detections, setDetections] = useState();
  const [file, setFile] = useState();
  const [hasError, setHasError] = useState();

  const onFileSelect = async (image) => {
    if (!image) return;

    setFile(image);
    const { data } = await estimateFace(image?.raw);
    const estimations = data.images_estimations[0]?.estimations;

    if (estimations?.length === 0) {
      setHasError(true);
      setFile(undefined);
      return;
    }

    const result = fromAPI(estimations);
    setDetections(result.all);

    if (result.all?.length === 1) {
      setSelectedFace(result.all[0]);
      setSelected(result.all[0].id);
    }
  };

  const onReset = () => {
    setHasError(false);
    setFile(undefined);
    setSelectedFace(undefined);
    setSelected(undefined);
    setDetections([]);
  };

  const onSelectHandler = useCallback(
    ([detectionId]) => {
      const face = detections?.find((detection) => detection?.id === detectionId);
      setSelectedFace(face);
      setSelected(face?.id);
    },
    [detections, setSelectedFace, setSelected],
  );

  return (
    <PhotoUploadForm
      detections={detections}
      errorProps={{ errors: { message: hasError && t('tasks:verification.error.face_missing') } }}
      file={file}
      onFileSelect={onFileSelect}
      onResetFile={onReset}
      onSelectSampleId={onSelectHandler}
    />
  );
}

export function VerificationForm() {
  const { t } = useTranslation();
  const [references, setReferences] = useState();
  const [candidates, setCandidates] = useState();
  const [result, setResult] = useState();

  const onSubmit = async () => {
    const {
      data: { matches },
    } = await apiContainer.lunaClient.matcher.raw({
      references: [
        {
          type: 'sdk_descriptor',
          id: references.id,
          data: references?.attributes?.descriptor?.sdk_descriptor,
        },
      ],
      candidates: [
        {
          type: 'sdk_descriptor',
          id: candidates.id,
          data: candidates?.attributes?.descriptor?.sdk_descriptor,
        },
      ],
    });
    setResult(matches?.[0].matches?.[0].similarity);
  };

  useEffect(() => {
    if (references === undefined || candidates === undefined) {
      setResult(undefined);
    }
  }, [references, candidates]);

  return (
    <Page>
      <h6>{t('externalID:action.verify')}</h6>
      <Divider small />
      <div className={st.Root}>
        <div className={st.Dropzone}>
          <ImageSelector setSelectedFace={setReferences} />
        </div>
        <div className={st.Dropzone1}>
          <ImageSelector setSelectedFace={setCandidates} />
        </div>

        <div className={st.Result}>
          {!!result && (
            <React.Fragment>
              <h6>{t('similarity')}</h6>
              <Divider small />
              <SimilarityTag
                value={result}
              />
            </React.Fragment>
          )}
        </div>

        <div className={st.Footer}>
          <Control.Button
            disabled={(references && candidates) === undefined}
            fullWidth
            onClick={onSubmit}
            type="button"
          >
            {t('externalID:action.verify')}
          </Control.Button>
        </div>
      </div>
    </Page>
  );
}
